import React from 'react';
import { motion } from 'framer-motion';
import { Handshake, GraduationCap, Building2, Users, Globe, Lightbulb, FileText, ArrowRight } from 'lucide-react';
export function PartnershipsPage() {
  const partnershipTypes = [{
    icon: <GraduationCap size={28} data-id="element-1372" />,
    title: 'Educational Institutions',
    color: 'bg-teal',
    description: 'Partner with universities, schools, and learning systems to integrate judgment development, reasoning quality, and civic capability into curricula and student development programs.'
  }, {
    icon: <Building2 size={28} data-id="element-1373" />,
    title: 'Employers & Organizations',
    color: 'bg-navy-light',
    description: 'Collaborate with companies and workforce programs to strengthen decision quality, leadership pipelines, and advancement readiness across teams and talent development initiatives.'
  }, {
    icon: <Users size={28} data-id="element-1374" />,
    title: 'Civil Society Organizations',
    color: 'bg-green-soft',
    description: 'Work with nonprofits, community groups, and mentorship networks to build misinformation resilience, opportunity pathways, and local leadership capacity.'
  }, {
    icon: <Globe size={28} data-id="element-1375" />,
    title: 'Government & Public Institutions',
    color: 'bg-navy',
    description: 'Support municipal, regional, and national governments in workforce modernization, civic resilience planning, civil service development, and leadership qualification frameworks.'
  }, {
    icon: <Lightbulb size={28} data-id="element-1376" />,
    title: 'Research Institutions',
    color: 'bg-teal',
    description: 'Engage with academic researchers, think tanks, and policy institutes to advance the science of judgment, decision-quality, manipulation resistance, and institutional readiness.'
  }, {
    icon: <FileText size={28} data-id="element-1377" />,
    title: 'Technology & Platform Partners',
    color: 'bg-navy-light',
    description: 'Integrate with complementary platforms, assessment tools, learning management systems, and HR technologies to expand reach and create seamless user experiences.'
  }];
  return <div className="min-h-screen bg-surface-bg" data-id="element-1378">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-1379">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-1380">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-1381">
            <span className="w-8 h-px bg-teal" data-id="element-1382"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-1383">
              Partnerships
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-1384"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-1385">
            Building the Future of Human Capability Together
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-1386">
            SyNeural is designed for collaboration. We partner with educational
            institutions, employers, civil society organizations, governments,
            research institutions, and technology platforms to strengthen
            judgment, expand opportunity, and build more resilient systems.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1387">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1388">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-1389">
            Our platform is most effective when integrated into broader
            ecosystems of development, opportunity, and institutional support.
            We work with partners who share our commitment to strengthening
            human capability, improving decision quality, and creating pathways
            from potential to responsibility.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1390">
            Whether you're an educational institution looking to strengthen
            reasoning and civic development, an employer focused on leadership
            pipelines, a government modernizing civil service, or a research
            organization advancing the science of judgment — we're interested in
            exploring how we can work together.
          </p>
        </motion.div>
      </section>

      {/* Partnership Types */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto" data-id="element-1391">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mb-16 text-center" data-id="element-1392">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1393">
            Partnership Opportunities
          </h2>
          <p className="text-lg text-ink-muted max-w-2xl mx-auto" data-id="element-1394">
            We're open to partnerships across multiple sectors and scales
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1395">
          {partnershipTypes.map((type, index) => <motion.div key={type.title} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} className="bg-white p-8 rounded-xl border border-line shadow-sm hover:shadow-md transition-shadow" data-id="element-1396">
              <div className={`w-14 h-14 rounded-lg ${type.color} flex items-center justify-center text-white mb-6`} data-id="element-1397">
                {type.icon}
              </div>
              <h3 className="text-xl font-heading font-bold text-ink-dark mb-3" data-id="element-1398">
                {type.title}
              </h3>
              <p className="text-ink-muted leading-relaxed" data-id="element-1399">
                {type.description}
              </p>
            </motion.div>)}
        </div>
      </section>

      {/* What We Offer Partners */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-1400">
        <div className="max-w-5xl mx-auto" data-id="element-1401">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1402">
            <h2 className="text-3xl font-heading font-bold text-ink-dark mb-12 text-center" data-id="element-1403">
              What We Offer Partners
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1404">
              <div className="bg-white p-8 rounded-lg border border-line" data-id="element-1405">
                <div className="w-12 h-12 rounded-lg bg-navy/10 flex items-center justify-center text-navy mb-4" data-id="element-1406">
                  <Handshake size={24} data-id="element-1407" />
                </div>
                <h3 className="text-xl font-heading font-bold text-ink-dark mb-3" data-id="element-1408">
                  Platform Access & Integration
                </h3>
                <p className="text-ink-muted leading-relaxed" data-id="element-1409">
                  Access to CJI and NCJI frameworks, customized deployment
                  models, API integration support, and technical collaboration.
                </p>
              </div>

              <div className="bg-white p-8 rounded-lg border border-line" data-id="element-1410">
                <div className="w-12 h-12 rounded-lg bg-teal/10 flex items-center justify-center text-teal mb-4" data-id="element-1411">
                  <Lightbulb size={24} data-id="element-1412" />
                </div>
                <h3 className="text-xl font-heading font-bold text-ink-dark mb-3" data-id="element-1413">
                  Research Collaboration
                </h3>
                <p className="text-ink-muted leading-relaxed" data-id="element-1414">
                  Joint research initiatives, pilot program support, data
                  insights, and co-development of frameworks and methodologies.
                </p>
              </div>

              <div className="bg-white p-8 rounded-lg border border-line" data-id="element-1415">
                <div className="w-12 h-12 rounded-lg bg-green-soft/10 flex items-center justify-center text-green-soft mb-4" data-id="element-1416">
                  <Users size={24} data-id="element-1417" />
                </div>
                <h3 className="text-xl font-heading font-bold text-ink-dark mb-3" data-id="element-1418">
                  Implementation Support
                </h3>
                <p className="text-ink-muted leading-relaxed" data-id="element-1419">
                  Training, onboarding assistance, best practices guidance, and
                  ongoing partnership management.
                </p>
              </div>

              <div className="bg-white p-8 rounded-lg border border-line" data-id="element-1420">
                <div className="w-12 h-12 rounded-lg bg-navy-light/10 flex items-center justify-center text-navy-light mb-4" data-id="element-1421">
                  <Globe size={24} data-id="element-1422" />
                </div>
                <h3 className="text-xl font-heading font-bold text-ink-dark mb-3" data-id="element-1423">
                  Strategic Alignment
                </h3>
                <p className="text-ink-muted leading-relaxed" data-id="element-1424">
                  Co-marketing opportunities, thought leadership collaboration,
                  and alignment on long-term vision and impact goals.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Partnership Principles */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1425">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1426">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-8 text-center" data-id="element-1427">
            Our Partnership Principles
          </h2>

          <div className="space-y-6" data-id="element-1428">
            <div className="flex gap-4" data-id="element-1429">
              <div className="w-2 h-2 rounded-full bg-teal mt-2 flex-shrink-0" data-id="element-1430"></div>
              <div data-id="element-1431">
                <h3 className="font-heading font-bold text-ink-dark mb-2" data-id="element-1432">
                  Shared Mission
                </h3>
                <p className="text-ink-muted leading-relaxed" data-id="element-1433">
                  We partner with organizations committed to strengthening human
                  capability, improving judgment quality, and creating
                  meaningful pathways for development and opportunity.
                </p>
              </div>
            </div>

            <div className="flex gap-4" data-id="element-1434">
              <div className="w-2 h-2 rounded-full bg-teal mt-2 flex-shrink-0" data-id="element-1435"></div>
              <div data-id="element-1436">
                <h3 className="font-heading font-bold text-ink-dark mb-2" data-id="element-1437">
                  Long-Term Orientation
                </h3>
                <p className="text-ink-muted leading-relaxed" data-id="element-1438">
                  We're focused on partnerships that create lasting value, not
                  short-term transactions. We invest in relationships that can
                  grow and deepen over time.
                </p>
              </div>
            </div>

            <div className="flex gap-4" data-id="element-1439">
              <div className="w-2 h-2 rounded-full bg-teal mt-2 flex-shrink-0" data-id="element-1440"></div>
              <div data-id="element-1441">
                <h3 className="font-heading font-bold text-ink-dark mb-2" data-id="element-1442">
                  Mutual Learning
                </h3>
                <p className="text-ink-muted leading-relaxed" data-id="element-1443">
                  The best partnerships are bidirectional. We learn from our
                  partners' expertise, context, and feedback to continuously
                  improve our platform and approach.
                </p>
              </div>
            </div>

            <div className="flex gap-4" data-id="element-1444">
              <div className="w-2 h-2 rounded-full bg-teal mt-2 flex-shrink-0" data-id="element-1445"></div>
              <div data-id="element-1446">
                <h3 className="font-heading font-bold text-ink-dark mb-2" data-id="element-1447">
                  Real-World Impact
                </h3>
                <p className="text-ink-muted leading-relaxed" data-id="element-1448">
                  We prioritize partnerships that translate into measurable
                  improvements in reasoning quality, opportunity access,
                  institutional readiness, and leadership development.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-navy-deep text-white" data-id="element-1449">
        <div className="max-w-4xl mx-auto text-center" data-id="element-1450">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1451">
            <h2 className="text-3xl font-heading font-bold mb-6" data-id="element-1452">
              Interested in Partnering with SyNeural?
            </h2>
            <p className="text-xl leading-relaxed opacity-90 mb-10" data-id="element-1453">
              We're always open to conversations with organizations that share
              our vision for strengthening human capability and institutional
              readiness.
            </p>
            <a href="#contact" className="inline-flex items-center gap-2 bg-white text-navy-deep font-medium px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors shadow-lg" data-id="element-1454">
              Get in Touch
              <ArrowRight size={20} data-id="element-1455" />
            </a>
          </motion.div>
        </div>
      </section>
    </div>;
}